(ns frontend.ux-overhaul.listing.generic.selection
  "Pure helpers for the selection of a listing.

   A selection is in one of two forms:
   - ids: the listing holds the ids of the selected rows in `:selected-ids`.
   - all: the user selected all rows of the grid. The listing holds the rows that
     the user then cleared in `:excluded-ids`, and the id of the grid. It does not
     know the ids of the selected rows. A selection action gets them with one
     request, at the time of the action, and the result goes into `:resolved-ids`."
  (:require
   [clojure.string :as string]
   [common.localization :as loc]))

(def max-all-selection-count
  "The largest number of rows that a selection of all rows can resolve to ids.
   It is the size limit of one OpenSearch result window."
  10000)

(defn all-selection?
  [selection]
  (true? (:all-selection? selection)))

(def ^:private user-selection-sources
  "The `source` values of an AG Grid selection event that a user action causes."
  #{"checkboxSelected"
    "rowClicked"
    "spaceKey"
    "keyboardSelectAll"
    "uiSelectAll"
    "uiSelectAllFiltered"
    "uiSelectAllCurrentPage"})

(defn user-selection-source?
  "True when the AG Grid selection event `source` is a user action."
  [source]
  (contains? user-selection-sources source))

(defn ids-selection
  "The selection that has only the rows with these ids. It removes every part of
   an earlier selection of all rows."
  [selection ids]
  (assoc selection
         :selected-ids (set ids)
         :all-selection? false
         :excluded-ids #{}
         :grid-id nil
         :resolved-ids nil
         :expected-count nil))

(defn all-selection
  "The selection that has all rows of the grid, except the rows with these ids."
  [selection {:keys [grid-id excluded-ids]}]
  (assoc selection
         :selected-ids #{}
         :all-selection? true
         :excluded-ids (set excluded-ids)
         :grid-id grid-id
         :resolved-ids nil
         :expected-count nil))

(defn selected-count
  "The number of selected rows. `grid-total` is the number of rows that the grid
   has for its current query."
  [selection grid-total]
  (if (all-selection? selection)
    (max 0 (- (or grid-total 0)
              (count (:excluded-ids selection))))
    (count (:selected-ids selection))))

(defn over-limit?
  [row-count]
  (> row-count max-all-selection-count))

(defn resolve-ids
  "The ids of the rows of a selection of all rows. `fetched-ids` are the ids of
   all rows of the grid."
  [{:keys [fetched-ids excluded-ids]}]
  (let [excluded (set excluded-ids)]
    (->> fetched-ids
         (remove nil?)
         (remove excluded)
         distinct
         vec)))

(defn store-resolved
  "Keep the resolved ids and the count that the user saw when the action started."
  [selection ids expected-count]
  (assoc selection
         :resolved-ids (set ids)
         :expected-count expected-count))

(defn action-ids
  "The ids that a selection action works on. For a selection of all rows these
   are the resolved ids, which exist only after the action resolved them."
  [selection]
  (->> (if (all-selection? selection)
         (:resolved-ids selection)
         (:selected-ids selection))
       (remove nil?)
       vec))

(defn delete-confirmation-body
  "The body of the confirmation before a delete. It shows how many rows the
   action deletes. It warns when this is not the number that the user saw."
  [selection]
  (let [ids-count (count (action-ids selection))
        expected-count (:expected-count selection)]
    [:div
     [:p (loc/tr :confirm-delete-selected)]
     [:p (loc/tr-plural {:translation-key :confirm-delete-selected-count
                         :quantity        ids-count})]
     (when (and (some? expected-count)
                (not= expected-count ids-count))
       [:p (loc/tr :confirm-delete-selected-count-changed)])]))

(defn pill-action-icon
  "The key of the icon of an action of the selection pill, or nil. A destructive
   action (the variant `:danger`, or an id that has `delete`) has the trash icon. An
   edit action (an id that has `edit`) has the pencil. The icon keys are in
   `ui.components.selection-pill.view/icon-elements`."
  [{:keys [id variant]}]
  (let [action-name (some-> id name)
        named? (fn [text]
                 (and action-name
                      (string/includes? action-name text)))]
    (cond
      (or (= :danger variant)
          (named? "delete")) :trash
      (named? "edit") :pencil
      :else nil)))
